import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { t } from 'ember-intl';
import OverrideIndicator from 'lumen/components/hotel/pricing-assistant/override-indicator';
import type { Occupancy, Period } from 'lumen/models';
import { calculateTotalRoomsForSale } from 'lumen/utils/pricing-assistant/dynamic-rate-pricing-utils';

import styles from './pricing-assistant-table-tooltip.module.css';

interface HotelPricingAssistantTableTooltipsTooltipOverbookingTableSignature {
  Args: {
    period: Period;
  };
}

export default class HotelPricingAssistantTableTooltipsTooltipOverbookingTable extends Component<HotelPricingAssistantTableTooltipsTooltipOverbookingTableSignature> {
  @service declare intl: Services['intl'];

  @service('restrictions/action-poller')
  declare restrictionsActionPoller: Services['restrictions/action-poller'];

  get hasOverbookingOverride(): boolean {
    return this.args.period.hasOverbookingOverride;
  }

  get isLoading(): boolean {
    const dateKey = this.args.period.fromDate.format('YYYY-MM-DD');
    return !!this.restrictionsActionPoller.datesBeingRefreshed[dateKey];
  }

  get occupancyData(): Occupancy | undefined {
    return this.args.period.day.occupancy;
  }

  get overbooking(): number | null {
    return this.args.period.effectiveOverbookingWithOverride;
  }

  get physicalRoomsAvailableLabel(): string {
    if (
      this.occupancyData &&
      this.occupancyData.inventoryExcludingOOORooms !== null
    ) {
      return this.intl.t(
        'pricing_manager.overbooking_tooltip.physical_rooms_available',
        {
          outOfOrder: this.occupancyData.roomsOutOfOrder,
        },
      );
    }

    return this.intl.t(
      'pricing_manager.overbooking_tooltip.physical_rooms_available',
      {
        outOfOrder: this.intl.t('general.missing'),
      },
    );
  }

  get physicalRoomsAvailableValue(): number | string {
    if (this.occupancyData && this.occupancyData.availableRooms !== null) {
      return this.occupancyData.availableRooms;
    }

    return this.intl.t('general.missing');
  }

  get roomsForSaleLabel(): string {
    if (this.overbooking !== null) {
      return this.intl.t('pricing_manager.overbooking.room_for_sale', {
        overbooking: this.overbooking,
      });
    }

    return this.intl.t('pricing_manager.overbooking.room_for_sale', {
      overbooking: this.intl.t('general.missing'),
    });
  }

  get roomsForSaleValue(): string | number {
    if (
      this.occupancyData &&
      this.occupancyData.availableRooms != null &&
      this.overbooking !== null
    ) {
      return calculateTotalRoomsForSale(
        this.occupancyData.availableRooms,
        this.overbooking,
      );
    }

    return this.intl.t('general.missing');
  }

  <template>
    <table class="default-table {{styles.table}}">
      <thead>
        <tr>
          <th class="{{styles.table-header}} text-200--bold">
            {{t "rates.booking_curve.inventory"}}
          </th>
        </tr>
      </thead>
      <tbody>
        {{! Row for Physical rooms available }}
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            <p
              class="text-200--regular"
            >{{this.physicalRoomsAvailableLabel}}</p>
            <div class="flex items-center">
              <p
                class="text-200--regular"
              >{{this.physicalRoomsAvailableValue}}</p>
              {{#if this.hasOverbookingOverride}}<span
                  class="invisible"
                ><OverrideIndicator /></span>{{/if}}
            </div>
          </td>
        </tr>
        {{! Row for rooms for sale }}
        <tr>
          <td class="{{styles.table-row-cell}} text-200--regular">
            {{#if this.isLoading}}
              <div class="loading-pulse w-5/6"></div>
              <div class="loading-pulse w-1/6"></div>
            {{else}}
              <p class="text-200--regular">{{this.roomsForSaleLabel}}</p>
              <div class="flex items-center">
                <p class="text-200--regular">{{this.roomsForSaleValue}}</p>
                {{#if this.hasOverbookingOverride}}<OverrideIndicator />{{/if}}
              </div>
            {{/if}}
          </td>
        </tr>
      </tbody>
    </table>
  </template>
}
